import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import type { User } from '@froma/shared';

// Helper to decode base64 (handles both standard and URL-safe base64)
function base64ToBuffer(base64: string): ArrayBuffer {
  // Convert URL-safe base64 to standard base64
  const standardBase64 = base64.replace(/-/g, '+').replace(/_/g, '/');
  // Add padding if needed
  const padded = standardBase64.padEnd(standardBase64.length + (4 - (standardBase64.length % 4)) % 4, '=');
  const binary = atob(padded);
  const buffer = new ArrayBuffer(binary.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i++) {
    view[i] = binary.charCodeAt(i);
  }
  return buffer;
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  error: string | null;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    isLoading: true,
    error: null,
  });

  const checkAuth = useCallback(async () => {
    try {
      const response = await api.getMe();
      if (response.data) {
        setState({ user: response.data, isLoading: false, error: null });
      } else {
        setState({ user: null, isLoading: false, error: null });
      }
    } catch {
      setState({ user: null, isLoading: false, error: null });
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email: string, password: string) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await api.login(email, password);
      if (response.data) {
        api.setToken(response.data.token);
        setState({ user: response.data.user, isLoading: false, error: null });
        return true;
      }
      return false;
    } catch (err) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: (err as Error).message,
      }));
      return false;
    }
  };

  const loginWithPasskey = async () => {
    if (!window.PublicKeyCredential) {
      setState((prev) => ({
        ...prev,
        error: 'Passkeys are not supported in this browser',
      }));
      return false;
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      // Get challenge from server
      const options = await api.getPasskeyChallenge();

      // Convert base64 to ArrayBuffer
      const challengeBuffer = base64ToBuffer(options.challenge);
      const allowCredentials = options.allowCredentials?.map((cred) => ({
        ...cred,
        id: base64ToBuffer(cred.id),
      }));

      // Get credential from authenticator
      const credential = (await navigator.credentials.get({
        publicKey: {
          challenge: challengeBuffer,
          timeout: options.timeout,
          rpId: options.rpId,
          allowCredentials: allowCredentials as PublicKeyCredentialDescriptor[],
          userVerification: options.userVerification as UserVerificationRequirement,
        },
      })) as PublicKeyCredential | null;

      if (!credential) {
        throw new Error('No credential returned');
      }

      const response = credential.response as AuthenticatorAssertionResponse;

      // Send to server for verification
      const verifyResponse = await api.verifyPasskey({
        id: credential.id,
        rawId: btoa(String.fromCharCode(...new Uint8Array(credential.rawId))),
        response: {
          clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(response.clientDataJSON))),
          authenticatorData: btoa(String.fromCharCode(...new Uint8Array(response.authenticatorData))),
          signature: btoa(String.fromCharCode(...new Uint8Array(response.signature))),
        },
        type: credential.type,
        sessionId: options.sessionId,
      });

      if (verifyResponse.data) {
        api.setToken(verifyResponse.data.token);
        setState({ user: verifyResponse.data.user, isLoading: false, error: null });
        return true;
      }
      return false;
    } catch (err) {
      const error = err as Error;
      let message = error.message;
      if (error.name === 'NotAllowedError') {
        message = 'Passkey authentication was cancelled';
      }
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: message,
      }));
      return false;
    }
  };

  const registerPasskey = async (name?: string) => {
    if (!window.PublicKeyCredential) {
      throw new Error('Passkeys are not supported in this browser');
    }

    // Get registration challenge from server
    const options = await api.getPasskeyRegisterChallenge();

    // Convert base64 to ArrayBuffer
    const challengeBuffer = base64ToBuffer(options.challenge);
    const userIdBuffer = base64ToBuffer(options.user.id);
    const excludeCredentials = options.excludeCredentials?.map((cred) => ({
      ...cred,
      id: base64ToBuffer(cred.id),
    }));

    // Create credential
    const credential = (await navigator.credentials.create({
      publicKey: {
        challenge: challengeBuffer,
        rp: options.rp,
        user: {
          id: userIdBuffer,
          name: options.user.name,
          displayName: options.user.displayName,
        },
        pubKeyCredParams: options.pubKeyCredParams as PublicKeyCredentialParameters[],
        timeout: options.timeout,
        attestation: options.attestation as AttestationConveyancePreference,
        authenticatorSelection: options.authenticatorSelection,
        excludeCredentials: excludeCredentials as PublicKeyCredentialDescriptor[],
      },
    })) as PublicKeyCredential | null;

    if (!credential) {
      throw new Error('No credential created');
    }

    const response = credential.response as AuthenticatorAttestationResponse;

    // Send to server to complete registration
    const result = await api.completePasskeyRegistration({
      id: credential.id,
      rawId: btoa(String.fromCharCode(...new Uint8Array(credential.rawId))),
      response: {
        clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(response.clientDataJSON))),
        attestationObject: btoa(String.fromCharCode(...new Uint8Array(response.attestationObject))),
      },
      type: credential.type,
      sessionId: options.sessionId,
      name,
    });

    return result.data;
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch {
      // Ignore logout errors
    }
    api.setToken(null);
    setState({ user: null, isLoading: false, error: null });
  };

  const clearError = () => {
    setState((prev) => ({ ...prev, error: null }));
  };

  return {
    user: state.user,
    isLoading: state.isLoading,
    error: state.error,
    login,
    loginWithPasskey,
    registerPasskey,
    logout,
    checkAuth,
    refreshUser: checkAuth,
    clearError,
  };
}
